import { Injectable } from '@angular/core';
import { BehaviorSubject, first, map, Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { NavigationService } from '../../navigation/service/navigation.service';
import { StatePersistenceService } from '../../shared/state-persistence.service';
import { GiftCardsApi } from '../api/gift-cards.api';
import { GiftCardItem } from '../model/gift-card-item';

const SESSION_STORAGE_KEY = 'gift-cards';

@Injectable({ providedIn: 'root' })
export class GiftCardService {
  private readonly giftCardItemsAddedToBasket$ = new BehaviorSubject<
    GiftCardItem[]
  >(
    this.statePersistenceService.getPersistedState(
      'giftCardItemsAddedToBasket',
      [],
      SESSION_STORAGE_KEY,
    ) as GiftCardItem[],
  );
  private readonly totalSum$ = new BehaviorSubject<number>(
    this.statePersistenceService.getPersistedState(
      'totalSum',
      0,
      SESSION_STORAGE_KEY,
    ) as number,
  );
  private readonly totalQuantity$ = new BehaviorSubject<number>(
    this.statePersistenceService.getPersistedState(
      'totalQuantity',
      0,
      SESSION_STORAGE_KEY,
    ) as number,
  );
  private readonly emailEntered$ = new BehaviorSubject<string>(
    this.statePersistenceService.getPersistedState(
      'emailEntered',
      '',
      SESSION_STORAGE_KEY,
    ) as string,
  );
  private readonly isAccountConfirmationUsedForEdit$ =
    new BehaviorSubject<boolean>(
      this.statePersistenceService.getPersistedState(
        'isAccountConfirmationUsedForEdit',
        false,
        SESSION_STORAGE_KEY,
      ) as boolean,
    );
  private readonly idempotencyKey$ = new BehaviorSubject<string | null>(
    this.statePersistenceService.getPersistedState(
      'idempotencyKey',
      null,
      SESSION_STORAGE_KEY,
    ) as string | null,
  );

  readonly giftCardItemsAddedToBasketAsObservable$ =
    this.giftCardItemsAddedToBasket$.asObservable();
  readonly totalSumAsObservable$ = this.totalSum$.asObservable();
  readonly totalQuantityAsObservable$ = this.totalQuantity$.asObservable();
  readonly isAccountConfirmationUsedForEditAsObservable$ =
    this.isAccountConfirmationUsedForEdit$.asObservable();

  giftCardAmountsInBasket: number[] =
    this.statePersistenceService.getPersistedState(
      'giftCardAmountsInBasket',
      [],
      SESSION_STORAGE_KEY,
    ) as number[];

  constructor(
    private readonly giftCardsApi: GiftCardsApi,
    private readonly statePersistenceService: StatePersistenceService,
    private readonly navigationService: NavigationService,
  ) {}

  setIdempotencyKey() {
    const newUuid = uuidv4();
    this.idempotencyKey$.next(newUuid);
    this.statePersistenceService.persistState(
      'idempotencyKey',
      newUuid,
      SESSION_STORAGE_KEY,
    );
  }

  clearSessionStorage() {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);

    this.idempotencyKey$.next(null);
    this.giftCardItemsAddedToBasket$.next([]);
    this.totalSum$.next(0);
    this.totalQuantity$.next(0);
    this.emailEntered$.next('');
    this.isAccountConfirmationUsedForEdit$.next(false);

    this.giftCardAmountsInBasket = [];
    this.statePersistenceService.persistState(
      'giftCardAmountsInBasket',
      [],
      SESSION_STORAGE_KEY,
    );
  }

  getDefaultAmounts(): Observable<number[]> {
    return this.giftCardsApi
      .getDefaultAmountsForGiftCards()
      .pipe(map((defaults) => defaults.map((x) => x.amount)));
  }

  addToBasket(amount: number): void {
    this.giftCardAmountsInBasket.push(amount);
    this.statePersistenceService.persistState(
      'giftCardAmountsInBasket',
      this.giftCardAmountsInBasket,
      SESSION_STORAGE_KEY,
    );

    const mappedAmounts = this.mapAmountsToItems(this.giftCardAmountsInBasket);
    this.giftCardItemsAddedToBasket$.next(mappedAmounts);
    this.statePersistenceService.persistState(
      'giftCardItemsAddedToBasket',
      mappedAmounts,
      SESSION_STORAGE_KEY,
    );
    this.calculateTotalSum(mappedAmounts);
    this.calculateTotalQuantity(mappedAmounts);
    this.setIdempotencyKey();
  }

  mapAmountsToItems(amounts: number[]): GiftCardItem[] {
    const amountSummary: Record<number, GiftCardItem> = {};

    for (const amount of amounts) {
      if (amountSummary[amount]) {
        amountSummary[amount].quantity += 1;
      } else {
        amountSummary[amount] = { amount, quantity: 1 };
      }
    }

    return Object.values(amountSummary);
  }

  calculateTotalSum(items: GiftCardItem[]): void {
    const totalSum = items.reduce(
      (sum, item) => sum + item.amount * item.quantity,
      0,
    );

    this.totalSum$.next(totalSum);
    this.statePersistenceService.persistState(
      'totalSum',
      totalSum,
      SESSION_STORAGE_KEY,
    );
    this.setIdempotencyKey();
  }

  calculateTotalQuantity(items: GiftCardItem[]): void {
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

    this.totalQuantity$.next(totalQuantity);
    this.statePersistenceService.persistState(
      'totalQuantity',
      totalQuantity,
      SESSION_STORAGE_KEY,
    );
    this.setIdempotencyKey();
  }

  deleteGiftCardItem(index: number): void {
    const items = Array.from(this.giftCardItemsAddedToBasket$.value);
    const { amount } = items[index];

    this.giftCardAmountsInBasket = this.giftCardAmountsInBasket.filter(
      (item) => item !== amount,
    );
    this.statePersistenceService.persistState(
      'giftCardAmountsInBasket',
      this.giftCardAmountsInBasket,
      SESSION_STORAGE_KEY,
    );

    items.splice(index, 1);
    this.giftCardItemsAddedToBasket$.next(items);

    this.statePersistenceService.persistState(
      'giftCardItemsAddedToBasket',
      items,
      SESSION_STORAGE_KEY,
    );

    this.calculateTotalSum(items);
    this.calculateTotalQuantity(items);
    this.setIdempotencyKey();
    this.checkIfBasketEmpty();
  }

  buildPasswordRequirements() {
    return {
      minChars: {
        label: $localize`8 characters`,
        fulfilled: false,
      },
      uppercase: {
        label: $localize`1 uppercase`,
        fulfilled: false,
      },
      lowercase: {
        label: $localize`1 lowercase`,
        fulfilled: false,
      },
      number: {
        label: $localize`1 number`,
        fulfilled: false,
      },
      specialChar: {
        label: $localize`1 special character`,
        fulfilled: false,
      },
    };
  }

  getEmailEntered() {
    return this.emailEntered$.value;
  }

  setIsAccountConfirmationUsedForEdit(b: boolean) {
    this.isAccountConfirmationUsedForEdit$.next(b);
    this.statePersistenceService.persistState(
      'isAccountConfirmationUsedForEdit',
      b,
      SESSION_STORAGE_KEY,
    );
  }

  setEmailEntered(email: string) {
    this.emailEntered$.next(email);
    this.statePersistenceService.persistState(
      'emailEntered',
      email,
      SESSION_STORAGE_KEY,
    );
  }

  getGiftCardItemsAddedToBasket() {
    return this.giftCardItemsAddedToBasket$.value;
  }

  getGiftCardItems() {
    return this.giftCardItemsAddedToBasket$.value;
  }

  getIsUsedForAccountConfirmation() {
    return this.isAccountConfirmationUsedForEdit$.value;
  }

  setGiftCardItems(items: GiftCardItem[]) {
    this.giftCardItemsAddedToBasket$.next(items);
    this.statePersistenceService.persistState(
      'giftCardItemsAddedToBasket',
      items,
      SESSION_STORAGE_KEY,
    );
    this.giftCardAmountsInBasket = items.flatMap((item) =>
      Array(item.quantity).fill(item.amount),
    );
    this.statePersistenceService.persistState(
      'giftCardAmountsInBasket',
      this.giftCardAmountsInBasket,
      SESSION_STORAGE_KEY,
    );
    this.setIdempotencyKey();
    this.checkIfBasketEmpty();
  }

  getIdempotencykey() {
    return this.idempotencyKey$.value;
  }

  checkIfBasketEmpty() {
    this.giftCardItemsAddedToBasket$.pipe(first()).subscribe((items) => {
      if (items.length === 0) {
        this.navigationService.navigateTo('/gift-cards');
      }
    });
  }
}
